import { CSSObject } from '@emotion/react';
import { getSizeValue, OptionCornerStyle, useHoneycombTheme } from '../../theming';

export interface OptionStylesParams {
    cornerStyle: OptionCornerStyle;
}

type OptionStyleClasses = 'root' | 'icon' | 'actionIcon';

export function useStyles({ cornerStyle }: OptionStylesParams): {
    styles: { [key in OptionStyleClasses]: CSSObject };
} {
    const { theme } = useHoneycombTheme();
    const { components, typography } = theme;
    const componentTheme = components.themes.Option;
    const fontFamily = typography.family.body;

    const { spacing, paddingY, paddingX, iconSize, actionIconSize, radius, fontSize, fontWeight, colors } =
        componentTheme;
    const iconSizeValue = getSizeValue(iconSize);
    const actionIconSizeValue = getSizeValue(actionIconSize);

    const radiusValue = cornerStyle === 'rounded' ? getSizeValue(radius) : undefined;
    const paddingXValue = getSizeValue(paddingX);
    const paddingYValue = getSizeValue(paddingY);

    const root: CSSObject = {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        gap: getSizeValue(spacing),
        paddingTop: paddingYValue,
        paddingBottom: paddingYValue,
        '&:not([data-default-gutters])': {
            paddingLeft: getSizeValue(paddingXValue),
            paddingRight: getSizeValue(paddingXValue),
        },
        backgroundColor: theme.fn.resolveColorValue(colors.background),
        borderRadius: radiusValue,
        fontSize: typography.variant.body[fontSize].l.fontSize,
        fontWeight: fontFamily.fontWeight[fontWeight],
        '&:not([data-selected])': {
            '&[role="button"], &[role="menuitem"], &[href]': theme.fn.hoverStyles({
                backgroundColor: theme.fn.resolveColorValue(colors.hoverBackground),
            }),
        },
        '&[data-selected]': {
            backgroundColor: theme.fn.resolveColorValue(colors.selectedBackground),
            color: theme.fn.resolveColorValue(colors.selectedText),
        },
        '> [data-content]': {
            flex: '1 1 auto',
        },
    };

    const icon = {
        width: iconSizeValue,
        height: iconSizeValue,
    };

    const actionIcon = {
        width: actionIconSizeValue,
        height: actionIconSizeValue,
    };

    return {
        styles: {
            root,
            icon,
            actionIcon,
        },
    };
}
